import { STATUS_BADGE_TONE_PALETTES, StatusBadge } from "@components";
import { Edit01, Settings04 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { type Action, type Column, TableCustom } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/use-permissions";
import { TruncatedText } from "@/shared/components/truncated-text";
import {
	usuarioDesativarMotorista,
	usuarioEditarMotorista,
	usuariosBuscar,
	usuariosNovo,
} from "@/services/analytics/events";
import { DeleteUser, EditUser, NewUser } from "../components";
import { useUsersQuery } from "../hooks";
import type { IUserModalState } from "../types";
import type { IUser } from "../types/user";

const roleLabels: Record<string, string> = {
	Driver: "Motorista",
	Manager: "Gestor de frota",
	Admin: "Administrador",
	TelemetryAdmin: "Administrador de telemetria",
	TelemetryMember: "Membro de telemetria",
};

const USER_STATUS_FILTERS = [
	{ id: "all", label: "Todos" },
	{ id: "active", label: "Ativos" },
	{ id: "inactive", label: "Inativos" },
] as const;

type UserStatusFilter = (typeof USER_STATUS_FILTERS)[number]["id"];

const UsersPageList = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("all");
	const { user } = useAuth();
	const { can } = usePermission();

	const isManager = user?.role?.includes("manager") ?? false;
	const canCreateUsers = can("users", "create");
	const canEditUsers = can("users", "edit");
	const canDeleteUsers = can("users", "delete");

	const isActiveFilter =
		statusFilter === "all" ? undefined : statusFilter === "active";

	const {
		data: users = [],
		isFetching,
		isError,
		refetch,
	} = useUsersQuery(isActiveFilter);

	const [modal, setModal] = useState<IUserModalState | null>(null);

	useEffect(() => {
		const handler = setTimeout(() => {
			if (searchTerm) {
				usuariosBuscar(searchTerm);
			}
		}, 500);
		return () => clearTimeout(handler);
	}, [searchTerm]);

	const columns: Column<IUser>[] = [
		{ id: "name", label: "Nome", minWidth: 150 },
		{
			id: "roles",
			label: "Tipo de usuário",
			minWidth: 150,
			render: (row) =>
				row.roles?.map((role) => roleLabels[role] || role).join(", ") || "",
		},
		{
			id: "groups",
			label: "Grupos",
			minWidth: 320,
			render: (row) => (
				<TruncatedText
					maxWidth={300}
					text={row.groups?.map((group) => group.name).join(", ")}
				/>
			),
		},
		{
			id: "isActive",
			label: "Status",
			minWidth: 150,
			render: (row) => (
				<StatusBadge
					label={row.isActive ? "Ativo" : "Desativado"}
					palette={
						row.isActive
							? STATUS_BADGE_TONE_PALETTES.success
							: STATUS_BADGE_TONE_PALETTES.error
					}
				/>
			),
		},
	];

	const actions: Action<IUser>[] = useMemo(() => {
		const list: Action<IUser>[] = [];

		if (canDeleteUsers) {
			list.push({
				icon: Settings04,
				label: (record: IUser) => (record.isActive ? "Desativar" : "Ativar"),
				disabled: (record: IUser) => {
					const recordIsAdmin = record.roles?.includes("Admin") ?? false;
					return isManager && recordIsAdmin;
				},
				onClick: (record) => {
					const recordIsAdmin = record.roles?.includes("Admin") ?? false;

					if (isManager && recordIsAdmin) {
						return;
					}

					usuarioDesativarMotorista(record.id);
					setModal({ type: "delete", user: record });
				},
			});
		}

		if (canEditUsers) {
			list.push({
				icon: Edit01,
				label: "Editar",
				onClick: (record) => {
					usuarioEditarMotorista(record.id, record.name);
					setModal({ type: "edit", user: record });
				},
			});
		}

		return list;
	}, [canDeleteUsers, canEditUsers, isManager]);

	const filteredUsers = useMemo(() => {
		const term = searchTerm.toLowerCase();

		return users.filter((row) => {
			const rolesText =
				row.roles?.map((role) => roleLabels[role] || role).join(", ") || "";
			const groupsText = row.groups?.map((g) => g.name).join(", ") || "";

			return (
				row.name.toLowerCase().includes(term) ||
				row.email.toLowerCase().includes(term) ||
				rolesText.toLowerCase().includes(term) ||
				groupsText.toLowerCase().includes(term)
			);
		});
	}, [searchTerm, users]);

	const closeModal = () => {
		setModal(null);
	};

	const handleAddUser = canCreateUsers
		? () => {
				usuariosNovo();
				setModal({ type: "new" });
			}
		: undefined;

	return (
		<>
			<TableCustom
				actions={actions}
				addButtonLabel="Cadastrar usuário"
				columns={columns}
				data={filteredUsers}
				errorMessage="Não foi possível carregar os usuários."
				filterBarLabel="Filtros de usuários"
				filterOptions={[...USER_STATUS_FILTERS]}
				filterPlaceholder="Filtrar por status"
				filterValue={statusFilter}
				isError={isError}
				isLoading={isFetching}
				layout="page"
				noDataMessage="Você não possui usuários cadastrados ainda."
				onAdd={handleAddUser}
				onFilterChange={(value) => {
					setStatusFilter(value as UserStatusFilter);
				}}
				onRetry={() => {
					refetch();
				}}
				onSearchChange={(value) => {
					usuariosBuscar(value);
					setSearchTerm(value);
				}}
				searchPlaceholder="Pesquise nome, e-mail ou perfil"
				searchValue={searchTerm}
				title="Usuários"
			/>

			{modal?.type === "new" && (
				<NewUser
					isOpen={true}
					onClose={() => setModal(null)}
					onSuccess={closeModal}
				/>
			)}

			{modal?.type === "edit" && modal.user && (
				<EditUser
					isOpen={true}
					onClose={() => setModal(null)}
					onSuccess={closeModal}
					user={modal.user}
				/>
			)}

			{modal?.type === "delete" && modal.user && (
				<DeleteUser
					isOpen={true}
					onClose={() => setModal(null)}
					onSuccess={closeModal}
					user={modal.user}
				/>
			)}
		</>
	);
};

export default UsersPageList;
